/**
 * BookingCalendar — dark-themed month calendar with color-coded court availability.
 *
 * Availability status thresholds (% of total slot minutes still free):
 *   Green  ≥ 60% — Plenty of open time
 *   Yellow 35–59% — Moderate availability
 *   Orange 10–34% — Few slots remaining
 *   Red     < 10% — Nearly or fully booked
 *   Gray  (closed) — Court closed / blocked on this day
 *
 * Props:
 *   value       — selected date YYYY-MM-DD
 *   onChange    — called with YYYY-MM-DD when a day is clicked
 *   slug        — facility slug (for API calls)
 *   T           — theme object from SportsPublicPage
 *   courtId     — (optional) filter to a specific court's availability
 *   minDate     — (optional, default = today) earliest selectable date
 */
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DOW = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const STATUS_DOT = {
  green:  '#22c55e',
  yellow: '#eab308',
  orange: '#f97316',
  red:    '#ef4444',
};

const LEGEND = [
  ['#22c55e','Open'],
  ['#eab308','Limited'],
  ['#f97316','Few slots'],
  ['#ef4444','Full'],
];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function BookingCalendar({ value, onChange, slug, T, courtId = null, minDate = null, maxDate = null }) {
  const effectiveMin = minDate || todayStr();

  const initFromValue = () => {
    const d = value ? new Date(value + 'T00:00:00') : new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  };

  const [viewYear, setViewYear] = useState(() => initFromValue().year);
  const [viewMonth, setViewMonth] = useState(() => initFromValue().month); // 0-indexed

  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    const params = { year: viewYear, month: viewMonth + 1 };
    if (courtId) params.court_id = courtId;
    axios.get(`${BACKEND}/api/sports/public/${slug}/monthly-availability`, { params })
      .then(r => { if (!cancelled) setAvailability(r.data || {}); })
      .catch(() => { if (!cancelled) setAvailability({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, viewYear, viewMonth, courtId]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  // Build grid: leading empty cells + day numbers
  const firstDow = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = Array(firstDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const fmt = d =>
    `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const today = todayStr();

  return (
    <div
      data-testid="sports-booking-calendar"
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        padding: '14px 12px 12px',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* ── Month header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button
          onClick={prevMonth}
          style={{ background: 'none', border: 'none', color: T.subtle, cursor: 'pointer', padding: '4px 6px', borderRadius: 6, lineHeight: 1 }}
          onMouseEnter={e => e.currentTarget.style.color = T.text}
          onMouseLeave={e => e.currentTarget.style.color = T.subtle}
        >
          <ChevronLeft size={15} />
        </button>

        <span style={{
          fontSize: 13, fontWeight: 700, color: loading ? T.subtle : T.text,
          fontFamily: 'Oswald, sans-serif', letterSpacing: 1.5, textTransform: 'uppercase',
          transition: 'color 0.2s',
        }}>
          {MONTHS[viewMonth]} {viewYear}
        </span>

        <button
          onClick={nextMonth}
          style={{ background: 'none', border: 'none', color: T.subtle, cursor: 'pointer', padding: '4px 6px', borderRadius: 6, lineHeight: 1 }}
          onMouseEnter={e => e.currentTarget.style.color = T.text}
          onMouseLeave={e => e.currentTarget.style.color = T.subtle}
        >
          <ChevronRight size={15} />
        </button>
      </div>

      {/* ── Day-of-week header ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 3 }}>
        {DOW.map(d => (
          <div key={d} style={{
            textAlign: 'center', fontSize: 9, fontWeight: 700,
            color: T.subtle, textTransform: 'uppercase', letterSpacing: 0.8,
            paddingBottom: 6,
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* ── Calendar grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`e-${idx}`} />;

          const dateStr = fmt(day);
          const isPast = dateStr < effectiveMin;
          const isBeyondMax = maxDate && dateStr > maxDate;
          const avail = availability[dateStr];
          const isClosed = avail?.status === 'closed';
          const isDisabled = isPast || isClosed || isBeyondMax;
          const isSelected = dateStr === value;
          const isToday = dateStr === today;
          const dotColor = avail && !isClosed ? STATUS_DOT[avail.status] : null;

          return (
            <button
              key={dateStr}
              data-testid={`sports-cal-day-${dateStr}`}
              disabled={isDisabled}
              onClick={() => !isDisabled && onChange(dateStr)}
              style={{
                position: 'relative',
                height: 36,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 7,
                border: isSelected
                  ? `1.5px solid ${T.primary}`
                  : isToday
                    ? `1px solid ${T.primary}55`
                    : '1px solid transparent',
                background: isSelected
                  ? T.primary + '28'
                  : isToday
                    ? T.primary + '12'
                    : 'transparent',
                color: isDisabled
                  ? T.subtle
                  : isSelected
                    ? T.primary
                    : T.text,
                cursor: isDisabled ? 'default' : 'pointer',
                fontSize: 11,
                fontWeight: isSelected || isToday ? 700 : 400,
                opacity: isPast ? 0.3 : isBeyondMax ? 0.3 : isClosed ? 0.45 : 1,
                transition: 'background 0.15s, border-color 0.15s',
                outline: 'none',
              }}
              onMouseEnter={e => {
                if (!isDisabled) e.currentTarget.style.background = isSelected ? T.primary + '28' : T.primary + '14';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = isSelected ? T.primary + '28' : isToday ? T.primary + '12' : 'transparent';
              }}
            >
              <span style={{ lineHeight: 1 }}>{day}</span>
              {dotColor && (
                <span style={{
                  position: 'absolute', bottom: 4,
                  width: 4, height: 4, borderRadius: '50%',
                  background: dotColor, display: 'block',
                }} />
              )}
            </button>
          );
        })}
      </div>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap', paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
        {LEGEND.map(([color, label]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9.5, color: T.subtle }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
            {label}
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9.5, color: T.subtle }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.subtle, display: 'inline-block', flexShrink: 0, opacity: 0.4 }} />
          Closed
        </div>
      </div>
    </div>
  );
}
